import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Truck, Sparkles, Package } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEUR } from "@/lib/api";
import { useT } from "@/context/I18nContext";

const HERO_IMG_FALLBACK = "https://images.unsplash.com/photo-1633360821154-1935fb5671e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STORY_IMG = "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const { t } = useT();
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [ugc, setUgc] = useState([]);
  const [heroImg, setHeroImg] = useState(HERO_IMG_FALLBACK);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/products/bestsellers", { params: { limit: 4 } }).then((r) => setBestsellers(r.data)).catch(() => {});
    api.get("/ugc", { params: { limit: 8 } }).then((r) => setUgc(r.data)).catch(() => {});
    api.get("/brand/home-hero").then((r) => { if (r.data?.url) setHeroImg(r.data.url); }).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden paper-grain" style={{ background: "linear-gradient(180deg, var(--brunchi-mint) 0%, var(--brunchi-paper) 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 py-16 lg:py-28">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[color:var(--brunchi-forest)] mb-5">
              <span>{t("home.hero.eyebrow_fresh")}</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full border-2 border-[color:var(--brunchi-green)]" />
              <span>{t("home.hero.eyebrow_natural")}</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full border-2 border-[color:var(--brunchi-green)]" />
              <span>{t("home.hero.eyebrow_smart")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[color:var(--brunchi-ink)]">
              {t("home.hero.title_1")}<br />
              {t("home.hero.title_2")} <span className="italic text-[color:var(--brunchi-green-hover)]">{t("home.hero.title_3")}</span> {t("home.hero.title_4")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[color:var(--brunchi-ink-soft)] leading-relaxed max-w-lg">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full pill-primary font-medium">
                {t("home.hero.explore")} <ArrowRight size={16} />
              </Link>
              <Link to="/bundle" data-testid="hero-bundle-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full pill-outline font-medium">
                <Package size={16} /> {t("home.hero.build_box")}
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { icon: <Leaf size={18} />, k: t("home.hero.eyebrow_fresh"), v: t("home.hero.feat_fresh_v") },
                { icon: <Truck size={18} />, k: t("home.hero.eyebrow_natural"), v: t("home.hero.feat_natural_v") },
                { icon: <Sparkles size={18} />, k: t("home.hero.eyebrow_smart"), v: t("home.hero.feat_smart_v") },
              ].map((f, i) => (
                <div key={i} className="text-xs">
                  <div className="text-[color:var(--brunchi-green-hover)] mb-1">{f.icon}</div>
                  <div className="font-display text-sm font-semibold">{f.k}</div>
                  <div className="text-[color:var(--brunchi-ink-soft)]">{f.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(76,184,70,0.18)]">
              <img src={heroImg} alt="Brunchi bars" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-12 -left-6 bg-white rounded-xl px-5 py-4 shadow-lg border border-[color:var(--brunchi-stone)] hidden sm:block">
              <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)]">{t("home.hero.free_cy")}</div>
              <div className="font-display text-2xl font-semibold">{t("home.hero.free_over")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Ribbon */}
      <section className="border-y border-[color:var(--brunchi-stone)] bg-[color:var(--brunchi-mint-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-hidden">
          <div className="flex flex-wrap gap-x-10 gap-y-2 justify-center text-xs uppercase tracking-[0.2em] font-medium text-[color:var(--brunchi-forest)]">
            <span>{t("home.ribbon.cy")}</span>
            <span>{t("home.ribbon.gr")}</span>
            <span>{t("home.ribbon.clean")}</span>
            <span>{t("home.ribbon.subs")}</span>
            <span>· EUR €</span>
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="home-featured">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-green-hover)] mb-2">{t("home.featured.eyebrow")}</div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold">{t("home.featured.title")}</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium hover:text-[color:var(--brunchi-green-hover)]">
              {t("home.featured.view_all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.map((p) => (
              <Link key={p.id} to={`/shop/${p.slug}`}
                className="group block bg-white rounded-xl overflow-hidden border border-[color:var(--brunchi-stone)] hover:shadow-lg transition"
                data-testid={`featured-product-${p.slug}`}>
                <div className="aspect-square overflow-hidden bg-[color:var(--brunchi-cream)]">
                  {p.images?.[0] && (
                    <img src={p.images[0]} alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display text-base sm:text-lg leading-tight">{p.name}</div>
                  <div className="mt-1 font-mono text-sm text-[color:var(--brunchi-green-hover)] font-semibold">
                    {fmtEUR(p.price_eur)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Pledge strip — no added sugar / handmade premium */}
      <section className="py-10 bg-[color:var(--brunchi-forest)] text-white" data-testid="home-pledge">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-mint-2)] mb-2">{t("home.pledge.eyebrow")}</div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-white" style={{ color: "#ffffff" }}>
              {t("home.pledge.no_sugar")}
            </h3>
          </div>
          <div className="md:col-span-2 space-y-3 text-white/85 text-base leading-relaxed">
            <p><strong className="text-[color:var(--brunchi-mint-2)]">{t("home.pledge.sweetened")}.</strong></p>
            <p>{t("home.pledge.handmade")}</p>
          </div>
        </div>
      </section>
      {bestsellers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="home-bestsellers">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-green-hover)] mb-2">{t("home.best.eyebrow")}</div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold">{t("home.best.title")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {bestsellers.map((p, i) => (
              <Link key={p.id} to={`/shop/${p.slug}`}
                className="group block bg-white rounded-xl overflow-hidden border border-[color:var(--brunchi-stone)] hover:shadow-lg relative"
                data-testid={`bestseller-${p.slug}`}>
                <div className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[color:var(--brunchi-green)] text-white">#{i + 1}</div>
                <div className="aspect-square overflow-hidden bg-[color:var(--brunchi-cream)]">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                </div>
                <div className="p-4">
                  <div className="font-display text-base sm:text-lg leading-tight">{p.name}</div>
                  <div className="mt-1 font-mono text-sm text-[color:var(--brunchi-green-hover)] font-semibold">{fmtEUR(p.price_eur)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* UGC Wall */}
      {ugc.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-ugc">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-green-hover)] mb-2">{t("home.ugc.eyebrow")}</div>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold mb-6">{t("home.ugc.title")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {ugc.map((u) => (
              <a key={u.id} href={u.link || "#"} target="_blank" rel="noreferrer"
                className="block aspect-square overflow-hidden rounded-lg bg-[color:var(--brunchi-cream)]"
                data-testid={`ugc-${u.id}`}>
                <img src={u.image_url} alt={u.caption || ""} className="w-full h-full object-cover hover:scale-105 transition duration-300" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Story */}
      <section id="craft" className="bg-[color:var(--brunchi-forest)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden aspect-[4/5]">
              <img src={STORY_IMG} alt="Kitchen" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-mint-2)] mb-4">{t("home.story.eyebrow")}</div>
            <h2 className="font-serif italic text-4xl lg:text-6xl font-normal leading-tight text-white">
              {t("home.story.title")}
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-2xl">
              {t("home.story.body")}
            </p>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[color:var(--brunchi-forest)] font-medium hover:bg-[color:var(--brunchi-mint)]"
              data-testid="story-shop-btn">
              {t("home.story.cta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
